/**
 * Q2 — "How do you compare to the market?" (SYR-68, Phase C).
 *
 * Two cards: best elements vs benchmark and worst, each a list of per-element
 * rows. Each row uses the Benchmarking bullet indicator (track + brand-value bar
 * coloured by performance + grey industry-mean marker) with the +/- difference
 * under a "vs. Benchmark" caption. Clicking a row opens the element drawer (same
 * as every other element row across the view); rows with no drawer node fall
 * back to a deep-link into The Benchmarks (records a Back-bar return point).
 */

import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, Eyebrow } from '../atoms';
import { LockedOverlay } from '../atoms/LockedOverlay';
import { SectionHead } from './SectionHead';
import { BAND_PALETTE } from '../config/bandColours';
import { getPerformanceLevel } from '../../charts/benchmarkThresholds';
import { getDisplayName } from '../../../utils/elementDisplayNames';
import type { ScoreNode } from '../types';
import type { BriefingBenchmarkMetric } from '../useBriefingData';

export interface Q2BenchmarkProps {
  metrics: BriefingBenchmarkMetric[];
  /** Overall % above/below industry average (for the lead-in). */
  overallDiff: number | null;
  /** Open the element drawer (same as every other element row across the view). */
  onOpen: (node: ScoreNode) => void;
  /** canonical → ScoreNode, to resolve a benchmark row to its drawer node. */
  nodeByCanonical: Map<string, ScoreNode>;
  /** Fallback deep-link to The Benchmarks when a row has no drawer node
   *  (level 1 = latent, 2 = measured). */
  onElementClick: (categoryName: string, level: number) => void;
  /** Tier-locked: blur the body behind an upgrade CTA (title stays visible). */
  locked?: boolean;
}

interface Row {
  name: string;
  value: number;
  industryMean: number;
  delta: number;
  level: number;
}

function deltaOf(m: BriefingBenchmarkMetric): number | null {
  if (typeof m.vsIndustryMean === 'number') return m.vsIndustryMean;
  if (typeof m.value === 'number' && typeof m.industryMean === 'number') {
    return m.value - m.industryMean;
  }
  return null;
}

const fmtDiff = (d: number): string => `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`;

/**
 * Per-row "bullet": light track + brand-value bar (coloured by performance vs
 * benchmark) + a grey industry-mean marker — the same indicator as the
 * Benchmarking bullet chart. The right column shows the +/- difference under a
 * "vs. Benchmark" caption (instead of the absolute value).
 */
const BenchRow: React.FunctionComponent<{
  row: Row;
  scaleMax: number;
  onClick: () => void;
}> = ({ row, scaleMax, onClick }) => {
  const color = getPerformanceLevel(row.delta).color;
  const valuePct =
    scaleMax > 0 ? Math.max(2, Math.min(100, (row.value / scaleMax) * 100)) : 0;
  const markerPct =
    scaleMax > 0 ? Math.max(0, Math.min(100, (row.industryMean / scaleMax) * 100)) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.5fr auto',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        border: 'none',
        background: 'transparent',
        padding: '10px 0',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: '#0F172A' }}>
        {getDisplayName(row.name)}
      </span>
      <span
        style={{
          position: 'relative',
          height: 18,
          background: '#F1F5F9',
          borderRadius: 4,
          display: 'block',
        }}
      >
        <span
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${valuePct}%`,
            background: color,
            borderRadius: 4,
            display: 'block',
          }}
        />
        <span
          style={{
            position: 'absolute',
            left: `${markerPct}%`,
            top: -2,
            height: 'calc(100% + 4px)',
            width: 3,
            background: '#94A3B8',
            transform: 'translateX(-1.5px)',
            display: 'block',
          }}
        />
      </span>
      <span style={{ justifySelf: 'end', textAlign: 'right', display: 'block', minWidth: 72 }}>
        <span
          style={{
            display: 'block',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#94A3B8',
          }}
        >
          <FormattedMessage
            id="marketerView.q2bench.vsBenchmark"
            defaultMessage="vs. Benchmark"
          />
        </span>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color }}>
          {fmtDiff(row.delta)}
        </span>
      </span>
    </button>
  );
};

export const Q2Benchmark: React.FunctionComponent<Q2BenchmarkProps> = ({
  metrics,
  overallDiff,
  onOpen,
  nodeByCanonical,
  onElementClick,
  locked = false,
}) => {
  // Prefer opening the element drawer (parity with every other element row);
  // fall back to the Benchmarks deep-link only when the row has no drawer node.
  const openRow = (row: Row) => {
    const node = nodeByCanonical.get(row.name);
    if (node) onOpen(node);
    else onElementClick(row.name, row.level);
  };
  const { widest, thinnest, scaleMax } = useMemo(() => {
    const rows: Row[] = metrics
      .filter(
        (m) =>
          m.level >= 1 &&
          typeof m.industryMean === 'number' &&
          (m.industryMean as number) > 0,
      )
      .map((m) => {
        const delta = deltaOf(m);
        return delta === null
          ? null
          : {
              name: m.categoryName,
              value: m.value,
              industryMean: m.industryMean as number,
              delta,
              level: m.level,
            };
      })
      .filter((r): r is Row => r !== null);
    // Best → worst by signed delta. Worst excludes any element already shown as
    // "best" so the two cards never repeat an element (brands with < 8 elements).
    const sorted = [...rows].sort((a, b) => b.delta - a.delta);
    const top = sorted.slice(0, 4);
    const topNames = new Set(top.map((r) => r.name));
    const bottom = [...sorted]
      .reverse()
      .filter((r) => !topNames.has(r.name))
      .slice(0, 4);
    // Shared x-scale across both cards so bars are comparable (mirrors the
    // Benchmarking bullet chart's global max * 1.1 headroom).
    const max =
      rows.reduce((mx, r) => Math.max(mx, r.value, r.industryMean), 0) * 1.1 ||
      100;
    return { widest: top, thinnest: bottom, scaleMax: max };
  }, [metrics]);

  if (widest.length === 0 && !locked) return null;

  return (
    <section id="market" style={{ paddingTop: 44 }}>
      <SectionHead
        q="Q2"
        title={
          <FormattedMessage
            id="marketerView.q2bench.title"
            defaultMessage="How do you compare to the market?"
          />
        }
        sub={
          overallDiff != null ? (
            overallDiff >= 0 ? (
              <FormattedMessage
                id="marketerView.q2bench.sub.above"
                defaultMessage="Brand Equity is {diff} the industry average. Here's where your lead over the benchmark is widest, and where it's thin enough that the market could catch up."
                values={{
                  diff: <strong>{fmtDiff(overallDiff)} above</strong>,
                }}
              />
            ) : (
              <FormattedMessage
                id="marketerView.q2bench.sub.below"
                defaultMessage="Brand Equity is {diff} the industry average. Here's where you're closest to the benchmark, and where you're falling furthest behind."
                values={{
                  diff: <strong>{fmtDiff(overallDiff)} below</strong>,
                }}
              />
            )
          ) : undefined
        }
      />
      <LockedOverlay locked={locked}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
        <Card>
          <Eyebrow color={BAND_PALETTE.strong.fg}>
            <FormattedMessage
              id="marketerView.q2bench.widest"
              defaultMessage="Best elements vs. benchmark"
            />
          </Eyebrow>
          <div style={{ marginTop: 12 }}>
            {widest.map((r) => (
              <BenchRow
                key={r.name}
                row={r}
                scaleMax={scaleMax}
                onClick={() => openRow(r)}
              />
            ))}
          </div>
        </Card>
        <Card>
          <Eyebrow color={BAND_PALETTE.watch.fg}>
            <FormattedMessage
              id="marketerView.q2bench.thinnest"
              defaultMessage="Worst elements vs. benchmark"
            />
          </Eyebrow>
          <div style={{ marginTop: 12 }}>
            {thinnest.map((r) => (
              <BenchRow
                key={r.name}
                row={r}
                scaleMax={scaleMax}
                onClick={() => openRow(r)}
              />
            ))}
          </div>
        </Card>
      </div>
      </LockedOverlay>
    </section>
  );
};
